/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button as CrcButton, Checkbox, ConfirmationModal } from '@crc-evans/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import ProductivityMainlinePanel, {
  invalidateDprProductivityCachesForLog,
} from './ProductivityMainlinePanel';
import axiosConfig from '../../services/axiosConfig';

/**
 * Collapse row: mainline name on the left; Add Weld Details on the right (space-between).
 * Skip productivity for all mainlines is controlled globally above the cards.
 * Clicks here must not toggle the panel — parent uses collapsible="icon" so only the arrow expands.
 */
const ProductivityMainlineCollapseHeader = ({ pipelineTitle, getPanelApi }) => {
  const stopPanelToggle = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (e && typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  };
  return (
    <div
      role="presentation"
      onClick={stopPanelToggle}
      onMouseDown={stopPanelToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        width: '100%',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
      }}
    >
      <span
        style={{
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '15px',
          lineHeight: 1.3,
          flex: '1 1 auto',
          minWidth: 0,
          textAlign: 'left',
          paddingRight: '12px'
        }}
      >
        Mainline: {pipelineTitle}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          flexShrink: 0,
          marginLeft: 'auto'
        }}
      >
        <CrcButton
          label="Add Weld Details"
          variant="primary"
          size="small"
          icon={Plus}
          onClick={(ev) => {
            stopPanelToggle(ev);
            getPanelApi()?.openAddWeld?.();
          }}
          style={{ backgroundColor: '#F43F5E', borderColor: '#F43F5E' }}
        />
      </div>
    </div>
  );
};

/**
 * One DPR log per day (`dprLogId`); productivity still edits welds per mainline (`mainlineProductivity` on save).
 * Multiple mainlines: stacked sections (no per-mainline collapse). All panels share the same `productivityData`
 * from the day-level DPR log GET.
 */
const ProductivityLog = ({
  productivityData,
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  selectedPipeline,
  isLastDprSection,
  /** All SOWs (mainlines) for the project — from DPR page pipeline list */
  projectMainlines = null,
  /** Incremented when the user opens the Productivity tab — triggers GET /dpr-productivity (once + cache for other mainlines). */
  refreshToken = 0,
  isShiftSkipped = false
}) => {
  const panelApisRef = useRef({});
  const singlePanelRef = useRef(null);
  const dispatch = useDispatch();
  const [globalSkipConfirmOpen, setGlobalSkipConfirmOpen] = useState(false);
  const [isGlobalSaving, setIsGlobalSaving] = useState(false);
  const mainlines = Array.isArray(projectMainlines) ? projectMainlines : [];
  const skipStatesBySection = useSelector((state) => state.dprReducer?.skipStatesBySection ?? {});

  const allMainlinesProductivitySkipped = useMemo(
    () =>
      mainlines.length > 0 &&
      mainlines.every((ml) => Boolean(skipStatesBySection[ml.id]?.skipProductivity)),
    [mainlines, skipStatesBySection]
  );

  /** Use card layout even for a single mainline (consistent UI). */
  const useCardLayout = mainlines.length >= 1 && selectedPipeline?.id != null;

  const mainlineIdsKey = useMemo(
    () => mainlines.map((m) => m.id).filter((id) => id != null).join(','),
    [mainlines]
  );

  useEffect(() => {
    if (!refreshToken || !dprLogId) return;

    /** Panel refs attach after Collapse/header paint; retry until imperative API is reachable. */
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 30;
    const delayMs = 50;

    /** Tab click already ran GET /dpr-productivity + filled cache; only sync panel state (no cache bust). */
    const runRefresh = async () => {
      if (!useCardLayout) {
        const fn = singlePanelRef.current?.reloadProductivityData;
        if (typeof fn !== 'function') return false;
        return (await fn()) === true;
      }
      if (mainlines.length === 0) return true;
      const apis = mainlines.map((ml) => panelApisRef.current?.[ml.id]);
      if (apis.some((api) => !api || typeof api.reloadProductivityData !== 'function')) return false;
      for (let i = 0; i < apis.length; i += 1) {
        await apis[i].reloadProductivityData();
      }
      return true;
    };

    const loop = async () => {
      await new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      while (!cancelled && attempt < maxAttempts) {
        try {
          /* eslint-disable no-await-in-loop -- sequential retries until refs exist */
          if (await runRefresh()) return;
        } catch {
          /* keep retrying */
        }
        attempt += 1;
        await new Promise((resolve) => {
          setTimeout(resolve, delayMs);
        });
      }
    };

    void loop();

    return () => {
      cancelled = true;
    };
  }, [refreshToken, dprLogId, shift, useCardLayout, mainlineIdsKey]);

  const handleGlobalSave = async () => {
    if (isShiftSkipped) return;
    if (!dprLogId) return;
    setIsGlobalSaving(true);
    try {
      const parts = mainlines
        .map((ml) => panelApisRef.current?.[ml.id]?.getMainlineDailyWeldLogDraft?.())
        .filter(Boolean);
      const weldLogMetrics = mainlines.flatMap((ml) => {
        const metrics = panelApisRef.current?.[ml.id]?.getWeldLogMetricsDraft?.();
        return Array.isArray(metrics) ? metrics : [];
      });
      // Notes live on each `mainlineProductivity[]` item (`notes` from each panel), not on root `dprProductivity`.
      const payload = {
        shift,
        dprProductivity: {
          dprLogId,
          ...(weldLogMetrics.length > 0 ? { weldLogMetrics } : {}),
          mainlineProductivity: parts,
        },
      };
      await axiosConfig.put('/dpr-productivity', payload);
      invalidateDprProductivityCachesForLog(dprLogId, shift);
      const apis = mainlines.map((ml) => panelApisRef.current?.[ml.id]);
      if (apis[0]?.refreshFromServer) {
        await apis[0].refreshFromServer();
        for (let i = 1; i < apis.length; i += 1) {
          if (typeof apis[i]?.reloadProductivityData === 'function') await apis[i].reloadProductivityData();
        }
      }
      // IMPORTANT: do not auto-navigate to next section for now.
    } finally {
      setIsGlobalSaving(false);
    }
  };

  if (!useCardLayout) {
    return (
      <ProductivityMainlinePanel
        ref={singlePanelRef}
        productivityData={productivityData}
        onSave={onSave}
        isSubmitting={isSubmitting}
        shift={shift}
        dprLogId={dprLogId}
        sowId={sowId}
        selectedProject={selectedProject}
        selectedPipeline={selectedPipeline}
        isLastDprSection={isLastDprSection}
        isShiftSkipped={isShiftSkipped}
        compactChrome={false}
      />
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {mainlines.length > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '10px',
            border: '1px solid #E5E7EB'
          }}
        >
          <Checkbox
            label="Skip productivity (all mainlines)"
            checked={allMainlinesProductivitySkipped}
            onChange={(e) => {
              const checked = e?.target ? e.target.checked : e;
              if (!checked) {
                mainlines.forEach((ml) => {
                  dispatch(updateDprSkipState(ml.id, 'skipProductivity', false));
                });
                return;
              }
              setGlobalSkipConfirmOpen(true);
            }}
            borderColor="#00C9A7"
          />
        </div>
      )}

      <div className="productivity-mainline-stack" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {mainlines.map((ml) => {
          const key = String(ml.id);
          const title =
            ml.pipelineSection ||
            ml.name ||
            (ml.pipeStringId != null && ml.pipeStringId !== ''
              ? `Mainline ${ml.pipeStringId}`
              : `Mainline ${ml.id}`);

          return (
            <div
              key={key}
              style={{
                background: '#FFFFFF',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
                padding: '0 0 8px 0',
              }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #F3F4F6',
                  background: '#FAFAFA',
                }}
              >
                <ProductivityMainlineCollapseHeader
                  pipelineTitle={title}
                  getPanelApi={() => panelApisRef.current[ml.id]}
                />
              </div>
              <ProductivityMainlinePanel
                ref={(node) => {
                  if (node) panelApisRef.current[ml.id] = node;
                  else delete panelApisRef.current[ml.id];
                }}
                key={`${ml.id}-${shift}`}
                productivityData={productivityData && typeof productivityData === 'object' ? productivityData : {}}
                onSave={onSave}
                isSubmitting={isSubmitting}
                shift={shift}
                dprLogId={dprLogId}
                sowId={ml.id}
                selectedProject={selectedProject}
                selectedPipeline={ml}
                mainlineTitle={title}
                isLastDprSection={isLastDprSection}
                isShiftSkipped={isShiftSkipped}
                compactChrome
                hideFooterActions={mainlines.length > 1}
              />
            </div>
          );
        })}
      </div>

      {/* Global Save / Save & Next (one API call) */}
      {mainlines.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <CrcButton
            label={typeof isLastDprSection === 'boolean' ? (isLastDprSection ? 'Save section' : 'Save & Next') : 'Save'}
            variant="secondary"
            onClick={handleGlobalSave}
            disabled={isSubmitting || isGlobalSaving}
            style={{ height: '42px', padding: '0 32px', fontSize: '14px', fontWeight: 600 }}
          />
        </div>
      )}

      <ConfirmationModal
        title="Skip productivity (all mainlines)"
        message={`Are you sure you want to skip the Productivity log for all ${mainlines.length} mainline sections for this ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={globalSkipConfirmOpen}
        onConfirm={() => {
          mainlines.forEach((ml) => {
            dispatch(updateDprSkipState(ml.id, 'skipProductivity', true));
          });
          setGlobalSkipConfirmOpen(false);
        }}
        onCancel={() => setGlobalSkipConfirmOpen(false)}
        size="medium"
      />
    </div>
  );
};

export default ProductivityLog;
